import { useId, useState, type ReactNode } from 'react';
import { Button, Dialog, DialogContent, FormField, Input } from '@proerp/ui';

/**
 * Bestätigung vor einer zerstörenden Aktion — als echter Dialog statt `window.confirm`
 * (CLAUDE.md §9: Bestätigungen über `Dialog` aus `@proerp/ui`). Der native Dialog blockiert
 * den Browser-Tab, ignoriert das Design-System und lässt sich auf dem Tablet leicht
 * wegtippen; hier gibt es Titel, Folgentext und eine klar benannte Aktion.
 *
 * Lag zuerst modul-lokal im Transport-Feature und ist seit dem Baubericht-Umbau
 * (2026-07-30) hier app-weit verfügbar — zwei Kopien derselben Rückfrage wären genau
 * die Art Duplikat, die §6 vermeiden will. `features/transport/ConfirmDialog.tsx`
 * re-exportiert diese Fassung. Seit dem Review 2026-09-28 sind auch die letzten
 * `window.confirm`-Stellen hierauf umgestellt.
 *
 * `children` trägt den Folgentext (was passiert, was bleibt). `typeToConfirm` verlangt
 * vor großen Sammel-Löschungen das Eintippen eines Worts (erp-design-guideline.md §9.5:
 * „Tippe LÖSCHEN zum Bestätigen" ab mehr als 10 Datensätzen).
 */
/**
 * Ab wie vielen Datensätzen eine Sammel-Löschung das Eintippen von „LÖSCHEN"
 * verlangt (erp-design-guideline.md §9.5: „bei > 10 Records").
 */
export function needsTypedConfirmation(count: number): boolean {
  return count > 10;
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  isPending = false,
  typeToConfirm,
  isConfirmDisabled = false,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  isPending?: boolean;
  /** Wort, das vor dem Auslösen eingetippt werden muss (Groß-/Kleinschreibung egal). */
  typeToConfirm?: string;
  /** Zusätzlich sperren, z. B. solange eine Vorschau lädt oder fehlgeschlagen ist. */
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const inputId = useId();
  const [typed, setTyped] = useState('');
  const isConfirmBlocked =
    isConfirmDisabled ||
    (typeToConfirm !== undefined && typed.trim().toLocaleUpperCase('de') !== typeToConfirm.toLocaleUpperCase('de'));

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
    >
      <DialogContent
        title={title}
        description={description}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
              Abbrechen
            </Button>
            <Button variant="danger" type="button" onClick={onConfirm} isLoading={isPending} disabled={isConfirmBlocked}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        {children || typeToConfirm ? (
          <div className="flex flex-col gap-3 text-sm text-muted-foreground">
            {children}
            {typeToConfirm && (
              <FormField label={`Tippe ${typeToConfirm} zum Bestätigen`} htmlFor={inputId}>
                <Input
                  id={inputId}
                  value={typed}
                  autoComplete="off"
                  autoFocus
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !isConfirmBlocked && !isPending) onConfirm();
                  }}
                />
              </FormField>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
